import { Fragment, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from 'model/Auth';
import { Role } from 'model/Role';
import { Route, Page, routeSiteMap, routeLabelList, getRoleRoutePermissions } from 'model/Route';
import { mediaQurey } from 'helper/Style';

export type MenuProps = Readonly<{
  className?: string;
}>;

type UserRoleMenuProps = Readonly<{
  role: Role;
  className?: string;
}>;

type PageParam = { route: Route, page: Page, title: string };
type MenuParam = {
  main: Partial<PageParam> & { title: string };
  subList?: PageParam[];
};
const makeGeneralMenuMainParam = (route: Route, page?: string): PageParam => ({
  route,
  page: page || 'list',
  title: routeLabelList[route],
});
const makeGeneralMenuParam = (route: Route, subList?: PageParam[]): MenuParam => ({
  main: makeGeneralMenuMainParam(route),
  subList: [
    { route, page: 'list', title: '一覧' },
    { route, page: 'create', title: '作成' },
    ...(subList || []),
  ],
});
const menuParams: MenuParam[] = [
  makeGeneralMenuParam('ledger'),
  makeGeneralMenuParam('report'),
  { main: makeGeneralMenuMainParam('schedule') },
  makeGeneralMenuParam('member', [
    { route: 'line-relation', page: 'set', title: 'LINE連携' },
    { route: 'line-relation', page: 'code', title: 'LINEコード' },
  ]),
  makeGeneralMenuParam('role'),
  makeGeneralMenuParam('partner'),
  {
    main: { title: 'その他の設定' },
    subList: ['contractor', 'work-resource', 'industrial-waste', 'machinery', 'att', 'car', 'work-process'].map(
      (route) => makeGeneralMenuMainParam(route)
    ),
  },
];

const routeIconList: { [route in Route]: string } = {
  ledger: 'data',
  report: 'edit',
  schedule: 'calendar',
  arrangement: 'user-group',
  partner: 'client',
  member: 'user-group',
  role: 'role',
  other: 'setting',
};

const routePagePath = <T extends Route>(route: T, page: typeof routeSiteMap[T][number]): string =>
  (route === 'report' && page === 'create') ?
    `/${route}/${((date) => date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate())(new Date())}/create` :
    `/${route}/${page}`;

const getRoleMenuList = (role?: Role) =>
  role?.permissions.ledgerEdit
    ? { main: { page: routePagePath('ledger', 'create'), label: '＋ 新規台帳を作成' } } :
  (role?.permissions.workSupervise && role?.permissions.reportCreate)
    ? { main: { page: routePagePath('report', 'create'), label: '＋ 新規日報を作成' } } : {};

const style = {
  layout: {
    width: '24rem',
    borderRight: 'solid 0.1rem #ccc',
    transition: 'width 0.3s',
    [mediaQurey('min', 'pc')]: {
      '&[data-display="1"]': {
        width: '24rem',
      },
      '&[data-display="0"]': {
        width: '0',
      },
    },
    [mediaQurey('max', 'tablet')]: {
      '&[data-display="0"]': {
        width: '24rem',
      },
      '&[data-display="1"]': {
        width: '0',
      },
    },
    '& a': {
      textDecoration: 'none',
    },
  },
  open: {
    width: '24rem',
  },
  close: {
    width: '0',
  },
  contentArea: {
    overflowX: 'hidden' as const,
  },
  mainPanelArea: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '24rem',
    height: '10rem',
    backgroundColor: '#fafafa',
    borderBottom: 'solid 0.1rem #dddddd',
  },
  mainPanel: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '1rem',
    height: '4rem',
    padding: '0 2rem',
    backgroundColor: '#fff',
    border: 'solid 0.1rem #073f99',
    borderRadius: '2rem',
    color: '#073f99',
    fontSize: '1.6rem',
    fontWeight: '700',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#ddeeff',
    },
  },
  panel: {
    display: 'flex',
    alignItems: 'center',
    gap: '2rem',
    width: '24rem',
    padding: '2rem',
    color: '#000000',
    fontSize: '1.6rem',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
    '& img': {
      width: '2rem',
    },
  },
  toggleArrow: {
    marginLeft: 'auto',
  },
  pageListArea: {
    display: 'grid',
    gridTemplateRows: '0fr',
    transition: 'grid-template-rows 0.3s',
    '&[data-open="open"]': {
      gridTemplateRows: '1fr',
    },
    '&[data-open="close"]': {
      gridTemplateRows: '0fr',
    },
  },
  pageList: {
    display: 'flex',
    flexDirection: 'column' as const,
    overflowY: 'hidden' as const,
    '& > a': {
      padding: '1rem 1rem 1rem 3rem',
      backgroundColor: '#f8f8f8',
      color: '#000000',
      fontSize: '1.4rem',
      '&:hover': {
        backgroundColor: '#f0f0f0',
      },
    },
  },
  drawer: {
    position: 'absolute' as const,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    top: '1rem',
    left: 'calc(100% + 0.1rem)',
    width: '2rem',
    height: '4rem',
    borderRadius: '0 0.2rem 0.2rem 0',
    backgroundColor: '#dddddd',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#cccccc',
    },
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    [mediaQurey('min', 'pc')]: {
      '&[data-display="1"]': {
        backgroundImage: 'url(/image/icon/chevron-left.svg)',
      },
      '&[data-display="0"]': {
        backgroundImage: 'url(/image/icon/chevron-right.svg)',
      },
    },
    [mediaQurey('max', 'tablet')]: {
      '&[data-display="0"]': {
        backgroundImage: 'url(/image/icon/chevron-left.svg)',
      },
      '&[data-display="1"]': {
        backgroundImage: 'url(/image/icon/chevron-right.svg)',
      },
    },
  },
};

const MenuListItem = (props: MenuParam) => {
  const [opening, setOpening] = useState<'open' | 'close' | undefined>();
  const iconName = ((route) => routeIconList[route])(props.main.route || 'other');

  const changeOpening = () => {
    setOpening(opening === 'open' ? 'close' : 'open');
  };

  return (
    props.subList?.length ? (
      <Fragment>
        <div onClick={changeOpening} css={style.panel}>
          <img src={`/image/icon/${iconName}.svg`} />
          {props.main.title}
          <img src={`/image/icon/chevron-${opening === 'open' ? 'up' : 'down'}.svg`} css={style.toggleArrow} />
        </div>
        <div data-open={opening || ''} css={style.pageListArea}>
          <div css={style.pageList}>
            {props.subList.map(({ route, page, title }, pageIndex) => (
              <NavLink to={routePagePath(route, page)} key={pageIndex}>{title}</NavLink>
            ))}
          </div>
        </div>
      </Fragment>
    ) :
    (props.main.route && props.main.page) ? (
      <NavLink to={routePagePath(props.main.route, props.main.page)} css={style.panel}>
        <img src={`/image/icon/${iconName}.svg`} />
        {props.main.title}
      </NavLink>
    ) : null
  );
};

const UserRoleMenu = (props: UserRoleMenuProps) => {
  const [display, setDisplay] = useState<number>(1);
  const roleMenu = getRoleMenuList(props.role);
  const rolePermissions = getRoleRoutePermissions(props.role);
  const userRoleMenuParams = menuParams.flatMap((menuParam) => {
    const hasMainPage = (({ route, page }) => route && page && rolePermissions[route]?.includes(page))(menuParam.main);
    const subList = (menuParam.subList || []).filter(({ route, page }) => rolePermissions[route]?.includes(page));

    return (
      subList.length > 1 ?
        { main: menuParam.main, subList } :
      subList.length ?
        { main: { ...subList[0], title: menuParam.main.title } } :
      hasMainPage ?
        { main: menuParam.main } : []
    );
  });

  const changeDisplay = () => {
    setDisplay(1 - display);
  };

  return <div className={props.className} css={style.layout} data-display={`${display}`}>
    <div css={style.contentArea}>
      {roleMenu.main && (
        <div css={style.mainPanelArea}>
          <NavLink to={roleMenu.main.page} css={style.mainPanel}>{roleMenu.main.label}</NavLink>
        </div>
      )}
      <div>
        {userRoleMenuParams.map((menuParam, index) => <MenuListItem {...menuParam} key={index} />)}
      </div>
      <div css={style.drawer} onClick={changeDisplay} data-display={`${display}`}>
      </div>
    </div>
  </div>;
};

const Menu = (props: MenuProps) => {
  const auth = useAuth();
  const userRole = auth.user?.role;

  return userRole && <UserRoleMenu role={userRole} className={props.className} />;
};

export default Menu;
